import React, { Fragment, createRef, useState, ChangeEvent } from 'react'
import TextInput from 'tfs.form-text-input'

import NotificationInterface from '../../interfaces/client/Notification'

import { removePassengerInputName } from '../../config/forms'

import constants from '../../config/constants'

import getLabels from '../../utils/getLabels'
import getNotificationLabelFromCode from '../../utils/notifications/getNotificationLabelFromCode'

import AddSSR from '../../containers/AddSSR'
import GenderSelect from '../../containers/GenderSelect'
import LuggageSelect from '../../containers/LuggageSelect'
import PassengerSSRs from '../../containers/PassengerSSRs'
import EditBookingFormInfantRow from '../../containers/EditBookingFormInfantRow'

import CheckedIn from '../CheckedIn'
import FormRowSeparator from '../FormRowSeparator'
import ToggleButton from '../ToggleButton'
import FormCheckbox from '../FormCheckbox'

import styles from './EditBookingFormRow.module.scss'
import globalStyles from '../../styles/global.module.scss'

interface EditBookingFormComposeRowsPropsInterface {
  addSSR: Function
  validateDateOfBirth: Function
  dateValidator: Function
  removeNotification: Function
  selectPassengerForDeletion: Function
  deselectPassengerForDeletion: Function
  handleCheckOutPassenger: Function

  passengerId: string
  infantId: string
  pnr: string
  thirdPartyBookingNumber: string
  flightId: string
  firstName: string
  lastName: string

  dateOfBirth: string
  gender: string
  luggage: string
  rowId: number
  specialSSRNames: string[]

  isTravelClassShown: boolean
  hasInfant: boolean
  isDeletePassengersEnabled: boolean
  isPassengerSelectedForDeletion: boolean
  isPassengerCheckedIn: boolean

  travelClass?: string
  rowError?: NotificationInterface
  firstNameError?: NotificationInterface
  lastNameError?: NotificationInterface
  dateOfBirthError?: NotificationInterface
}

const { frontEndDateFormat } = constants

export default (props: EditBookingFormComposeRowsPropsInterface) => {
  const {
    passengerId,
    infantId,
    pnr,
    isTravelClassShown,
    hasInfant,
    travelClass,
    firstName,
    lastName,
    dateOfBirth,
    gender,
    luggage,
    rowId,
    thirdPartyBookingNumber,
    addSSR,
    specialSSRNames,
    firstNameError,
    lastNameError,
    dateOfBirthError,
    rowError,
    validateDateOfBirth,
    dateValidator,
    removeNotification,
    flightId,
    isPassengerCheckedIn,
    isDeletePassengersEnabled,
    isPassengerSelectedForDeletion,
    selectPassengerForDeletion,
    deselectPassengerForDeletion,
    handleCheckOutPassenger,
  } = props
  const [isAddSSRActive, setIsAddSSRActive] = useState(false)
  const [passenger, setPassenger] = useState({
    firstName,
    lastName,
    dateOfBirth,
    gender,
    luggage,
  })

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.currentTarget

    setPassenger({
      ...passenger,
      [String(target.dataset.name)]: target.value,
    })

    if (target.dataset.errorId) {
      removeNotification(target.dataset.errorId)
    }
  }

  const handleDateOfBirthChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleChange(event)
    validateDateOfBirth(
      event.currentTarget,
      passengerId,
      dateValidator,
      dateOfBirthError
    )
  }

  const handleDeleteCheckboxChange = (event: ChangeEvent<HTMLInputElement>) =>
    event.currentTarget.checked
      ? selectPassengerForDeletion(passengerId, infantId)
      : deselectPassengerForDeletion(passengerId, infantId)

  const renderCheckedIn = isPassengerCheckedIn && !isDeletePassengersEnabled

  return (
    <Fragment>
      {isDeletePassengersEnabled &&
        (!isPassengerCheckedIn ? (
          <FormCheckbox
            elementRef={createRef()}
            name={`${passengerId}-delete`}
            handleChange={handleDeleteCheckboxChange}
            checked={isPassengerSelectedForDeletion}
            data-element={removePassengerInputName}
            data-passenger-id={passengerId}
          />
        ) : (
          <span></span>
        ))}

      <span>{thirdPartyBookingNumber}</span>

      <span>{pnr}</span>

      <span className={globalStyles['input-wrapper']}>
        <TextInput
          elementRef={createRef()}
          name={`${passengerId}-first-name`}
          data-element={`${rowId}-first-name`}
          data-name="firstName"
          data-error-id={firstNameError ? firstNameError.id : ''}
          required={true}
          defaultValue={firstName}
          onChange={handleChange}
          data-changed={firstName !== passenger.firstName}
          minvalue={1}
          error={getNotificationLabelFromCode(firstNameError)}
          readOnly={isDeletePassengersEnabled || isPassengerCheckedIn}
        />
      </span>

      <span className={globalStyles['input-wrapper']}>
        <TextInput
          elementRef={createRef()}
          name={`${passengerId}-last-name`}
          data-element={`${rowId}-last-name`}
          data-name="lastName"
          data-error-id={lastNameError ? lastNameError.id : ''}
          required={true}
          defaultValue={lastName}
          onChange={handleChange}
          data-changed={lastName !== passenger.lastName}
          minvalue={1}
          error={getNotificationLabelFromCode(lastNameError)}
          readOnly={isDeletePassengersEnabled || isPassengerCheckedIn}
        />
      </span>

      <span className={globalStyles['input-wrapper']}>
        <TextInput
          elementRef={createRef()}
          name={`${passengerId}-date-of-birth`}
          data-name="dateOfBirth"
          data-error-id={dateOfBirthError ? dateOfBirthError.id : ''}
          placeholder={frontEndDateFormat}
          pattern="\d{2}-\d{2}-\d{4}"
          data-element={`${rowId}-date-of-birth`}
          defaultValue={dateOfBirth}
          onChange={handleDateOfBirthChange}
          data-changed={dateOfBirth !== passenger.dateOfBirth}
          error={getNotificationLabelFromCode(dateOfBirthError)}
          readOnly={isDeletePassengersEnabled || isPassengerCheckedIn}
        />
      </span>

      <span className={globalStyles['input-wrapper']}>
        <GenderSelect
          inputName={`${passengerId}-gender`}
          refName={`${rowId}-gender`}
          isRequired={true}
          elementRef={createRef()}
          isDisabled={isDeletePassengersEnabled || isPassengerCheckedIn}
          defaultValue={gender}
          onChange={handleChange}
          dataChanged={gender !== passenger.gender}
          dataName="gender"
        />
      </span>

      <span className={globalStyles['input-wrapper']}>
        <LuggageSelect
          refName={`${rowId}-luggage`}
          passengerId={passengerId}
          elementRef={createRef()}
          isDisabled={
            isDeletePassengersEnabled ||
            isTravelClassShown ||
            isPassengerCheckedIn
          }
          onChange={handleChange}
          dataChanged={luggage !== passenger.luggage}
          dataName="luggage"
        />
      </span>

      {isTravelClassShown && <span>{travelClass}</span>}

      <span
        data-element="ssrs-wrapper"
        className={globalStyles['input-wrapper']}
      >
        <ToggleButton
          isOpen={isAddSSRActive}
          addDataElement={`${rowId}-add-ssrs`}
          removeDataElement={`${rowId}-close-ssrs`}
          removeLabel={getLabels('close_ssr')}
          addLabel={getLabels('add_ssr')}
          handleClick={() => setIsAddSSRActive(!isAddSSRActive)}
          isDisabled={isDeletePassengersEnabled || isPassengerCheckedIn}
        />
      </span>

      {hasInfant && (
        <EditBookingFormInfantRow
          passengerId={passengerId}
          rowId={rowId}
          flightId={flightId}
          isPassengerCheckedIn={isPassengerCheckedIn}
        />
      )}

      <PassengerSSRs
        passengerId={passengerId}
        isDisabled={isDeletePassengersEnabled}
        readonly={false}
        startOnColumnThree={true}
        isPassengerCheckedIn={isPassengerCheckedIn}
      />

      {isAddSSRActive && (
        <div className={styles['add-ssr-wrapper']}>
          <AddSSR
            name={`${rowId}-add-ssr-input`}
            onConfirm={(id: string) => {
              addSSR(passengerId, id, specialSSRNames)
            }}
          />
        </div>
      )}

      {renderCheckedIn && (
        <div className={styles['total-row']}>
          <CheckedIn
            checkOutPassenger={handleCheckOutPassenger}
            passengerId={passengerId}
            flightId={flightId}
            rowId={rowId}
          />
        </div>
      )}

      {rowError && (
        <div
          data-element={`${rowId}-row-error`}
          className={`${styles['total-row']} ${styles['error-message']}`}
        >
          {getNotificationLabelFromCode(rowError)}
        </div>
      )}

      <FormRowSeparator />
    </Fragment>
  )
}
